import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Check, Sparkles, Car, Truck } from 'lucide-react';
import { Button } from './ui/button';

const ServicesSection = () => {
  const scrollToBooking = () => {
    document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const services = [
    {
      icon: Car,
      title: 'Interior Detailing',
      description: 'Deep clean and restoration for your vehicle\'s interior',
      pricing: [
        { vehicle: 'Cars', price: '$65 - $85' },
        { vehicle: 'SUVs', price: '$75 - $95' },
        { vehicle: 'Trucks/Large SUVs', price: '$85 - $115' }
      ],
      features: [
        'Complete vacuum of all surfaces',
        'Dashboard and console cleaning',
        'Door panel and trim detailing',
        'Window cleaning (interior)',
        'Floor mat cleaning',
        'Cup holder and vent cleaning'
      ],
      popular: false
    },
    {
      icon: Sparkles,
      title: 'Exterior Detailing',
      description: 'Premium exterior care for a showroom finish',
      pricing: [
        { vehicle: 'Cars', price: '$65 - $85' },
        { vehicle: 'SUVs', price: '$75 - $95' },
        { vehicle: 'Trucks/Large SUVs', price: '$85 - $115' }
      ],
      features: [
        'Hand wash with premium soap',
        'Wheel and tire cleaning',
        'Tire shine application',
        'Spray wax protection',
        'Window cleaning (exterior)'
      ],
      popular: true
    },
    {
      icon: Truck,
      title: 'Full Detail Package',
      description: 'Complete interior and exterior transformation',
      pricing: [
        { vehicle: 'Cars', price: '$130 - $170' },
        { vehicle: 'SUVs', price: '$150 - $190' },
        { vehicle: 'Trucks/Large SUVs', price: '$170 - $230' }
      ],
      features: [
        'All Interior Detailing services',
        'All Exterior Detailing services',
        'Complete vehicle transformation',
        'Best value for full service'
      ],
      popular: false
    }
  ];

  const addOns = [
    { name: 'Clay Bar Treatment', price: '$40', description: 'Removes embedded contaminants' },
    { name: 'Car Shampoo', price: '$40', description: 'Deep upholstery cleaning' },
    { name: 'Pet Hair Removal', price: '$30', description: 'Thorough pet hair extraction' },
    { name: 'Leather Conditioner', price: '$25', description: 'Protect and restore leather' }
  ];

  return (
    <section id="services-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black via-zinc-950 to-black">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Pick Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-400">Treatment</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Professional detailing services tailored to your vehicle's needs
          </p>
          <p className="text-yellow-400 text-sm mt-2 font-medium">
            *Price depends on size and condition
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <Card 
              key={index}
              className={`bg-zinc-900/50 backdrop-blur-sm border-2 ${
                service.popular 
                  ? 'border-yellow-500 shadow-lg shadow-yellow-500/20' 
                  : 'border-yellow-500/20'
              } hover:border-yellow-500 transition-all duration-300 hover:scale-105 relative overflow-hidden group`}
            >
              {service.popular && (
                <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-500 to-amber-400 text-black text-xs font-bold px-3 py-1 rounded-full">
                  POPULAR
                </div>
              )}
              
              <CardHeader>
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-yellow-500/20 to-amber-400/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <service.icon className="w-8 h-8 text-yellow-400" />
                </div>
                <CardTitle className="text-2xl text-white">{service.title}</CardTitle>
                <CardDescription className="text-gray-400">{service.description}</CardDescription>
              </CardHeader>
              
              <CardContent>
                {/* Pricing */}
                <div className="mb-6 space-y-2">
                  {service.pricing.map((price, idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">{price.vehicle}</span>
                      <span className="text-yellow-400 font-bold">{price.price}</span>
                    </div>
                  ))}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={scrollToBooking}
                  className={`w-full ${
                    service.popular
                      ? 'bg-gradient-to-r from-yellow-500 to-amber-400 hover:from-yellow-600 hover:to-amber-500 text-black'
                      : 'bg-zinc-800 hover:bg-zinc-700 text-yellow-400 border border-yellow-500/30'
                  } font-semibold transition-all duration-300`}
                >
                  Book Now
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add-ons Section */}
        <div className="mt-16">
          <h3 className="text-3xl font-bold text-white text-center mb-8">
            Premium <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-400">Add-Ons</span>
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {addOns.map((addon, index) => (
              <div 
                key={index}
                className="bg-zinc-900/50 backdrop-blur-sm border border-yellow-500/20 rounded-xl p-6 hover:border-yellow-500 transition-all duration-300 hover:scale-105"
              >
                <div className="flex justify-between items-start mb-3">
                  <h4 className="text-white font-semibold">{addon.name}</h4>
                  <span className="text-yellow-400 font-bold">{addon.price}</span>
                </div>
                <p className="text-gray-400 text-sm">{addon.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
